import React, { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Lock, Eye, EyeOff, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const passwordError = useMemo(() => {
    if (!password) return '';
    if (password.length < 8) return 'Password must be at least 8 characters';
    return '';
  }, [password]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!token) { setError('This reset link is invalid or incomplete.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }

    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setDone(true);
        toast.success('Password reset successfully');
      } else {
        setError(data.detail || `Request failed (${res.status})`);
      }
    } catch {
      setError('Unable to connect. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12 bg-background" data-testid="reset-password-page">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card shadow-xl overflow-hidden">
        <div className="pt-10 px-8 pb-2">
          <h1 className="text-2xl font-bold tracking-tight text-foreground" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Set a new password</h1>
          <p className="text-sm mt-2 text-muted-foreground">
            {done ? 'Your password has been updated.' : 'Choose a strong new password for your account.'}
          </p>
        </div>

        <div className="px-8 pb-10 pt-4">
          {done ? (
            <div className="space-y-5" data-testid="reset-password-success">
              <div className="flex flex-col items-center justify-center py-6 text-center">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mb-4 bg-emerald-500/10 border border-emerald-500/20">
                  <CheckCircle2 className="h-7 w-7 text-emerald-500" />
                </div>
                <h3 className="text-lg font-semibold mb-2 text-foreground">Password updated</h3>
                <p className="text-sm max-w-xs text-muted-foreground">You can now sign in with your new password.</p>
              </div>
              <Button type="button" className="w-full h-11 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
                onClick={() => navigate('/login', { replace: true })} data-testid="reset-return-to-login-button">
                Return to Login
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="reset-password-form">
              {!token && (
                <Alert className="bg-destructive/10 border-destructive/20" data-testid="reset-invalid-token">
                  <AlertCircle className="h-4 w-4 text-destructive" />
                  <AlertDescription className="text-destructive text-sm">This reset link is invalid or incomplete. Request a new one from the login page.</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="new-password" className="text-sm font-medium text-foreground/70">New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input id="new-password" type={show ? 'text' : 'password'} placeholder="At least 8 characters"
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 h-11 rounded-xl" disabled={loading} autoFocus data-testid="reset-password-input" />
                  <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" tabIndex={-1}>
                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {passwordError && <p className="text-xs text-destructive flex items-center gap-1"><AlertCircle className="h-3 w-3" />{passwordError}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password" className="text-sm font-medium text-foreground/70">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input id="confirm-password" type={show ? 'text' : 'password'} placeholder="Re-enter password"
                    value={confirm} onChange={(e) => setConfirm(e.target.value)}
                    className="pl-10 h-11 rounded-xl" disabled={loading} data-testid="reset-confirm-input" />
                </div>
              </div>

              {error && <p className="text-xs text-destructive flex items-center gap-1" data-testid="reset-password-error"><AlertCircle className="h-3 w-3" />{error}</p>}

              <Button type="submit" className="w-full h-11 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
                disabled={loading || !token} data-testid="reset-password-submit">
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Updating...</> : 'Reset Password'}
              </Button>

              <p className="text-center text-sm pt-2 text-muted-foreground">
                <button type="button" onClick={() => navigate('/login')} className="text-primary font-medium hover:underline focus:outline-none" data-testid="reset-back-to-login">Back to Login</button>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
